/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useState, useEffect, useCallback } from 'react';
import { SOC2Scenario, ScenarioObjective, PlayerDecision } from '../../types/soc2';
import { SOC2ControlManager } from '../../services/soc2ControlManager';

interface SOC2ScenarioPlayerProps {
  scenario: SOC2Scenario;
  budget: number;
  onComplete: (result: any) => void;
  onClose: () => void;
}

const SOC2ScenarioPlayer: React.FC<SOC2ScenarioPlayerProps> = ({
  scenario,
  budget,
  onComplete,
  onClose
}) => {
  const [soc2Manager] = useState(() => new SOC2ControlManager());
  const [gameState, setGameState] = useState<GameState>({
    timeRemaining: scenario.timeLimit * 60, // Convert to seconds
    budgetRemaining: scenario.costBudget,
    decisions: [],
    objectives: scenario.objectives.map(obj => ({ ...obj, currentValue: 0 })),
    currentPhase: 'planning',
    score: 0,
    securityScore: 50,
    complianceScore: 50,
    riskLevel: 50,
    businessImpact: 100
  });

  const [activeDecision, setActiveDecision] = useState<DecisionState | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [selectedControls, setSelectedControls] = useState<string[]>([]);

  useEffect(() => {
    if (gameState.timeRemaining > 0 && gameState.currentPhase !== 'completed') {
      const timer = setTimeout(() => {
        setGameState(prev => ({
          ...prev,
          timeRemaining: Math.max(0, prev.timeRemaining - 1)
        }));
      }, 1000);

      return () => clearTimeout(timer);
    } else if (gameState.timeRemaining === 0 && gameState.currentPhase !== 'completed') {
      completeScenario();
    }
  }, [gameState.timeRemaining, gameState.currentPhase]);

  const handleControlSelection = (controlId: string) => {
    setSelectedControls(prev =>
      prev.includes(controlId)
        ? prev.filter(id => id !== controlId)
        : [...prev, controlId]
    );
  };

  const makeDecision = (decision: PlayerDecision) => {
    const control = soc2Manager.getControl(decision.controlId);
    if (!control) return;

    const gameStateUpdate = calculateDecisionImpact(decision, control);

    setGameState(prev => ({
      ...prev,
      budgetRemaining: Math.max(0, prev.budgetRemaining - decision.cost),
      timeRemaining: Math.max(0, prev.timeRemaining - Math.floor(decision.timeSpent / 60)),
      decisions: [...prev.decisions, decision],
      ...gameStateUpdate
    }));

    // Update objective progress
    const updatedObjectives = gameState.objectives.map(obj => {
      if (obj.id === 'obj1' && decision.action === 'implement') {
        return { ...obj, currentValue: obj.currentValue + 1 };
      }
      return obj;
    });

    setGameState(prev => ({ ...prev, objectives: updatedObjectives }));
    setActiveDecision(null);
  };

  const calculateDecisionImpact = (decision: PlayerDecision, control: any) => {
    let securityImpact = 0;
    let complianceImpact = 0;
    let riskReduction = 0;
    let businessImpactChange = 0;

    switch (control.category) {
      case 'security':
        securityImpact = 15;
        complianceImpact = 8;
        break;
      case 'confidentiality':
      case 'privacy':
        securityImpact = 12;
        complianceImpact = 18;
        break;
      case 'availability':
        securityImpact = 10;
        complianceImpact = 12;
        break;
      case 'processing_integrity':
        securityImpact = 8;
        complianceImpact = 15;
        break;
      default:
        securityImpact = 10;
        complianceImpact = 10;
    }

    // Apply scenario-specific modifiers
    if (scenario.category === 'risk_assessment') {
      securityImpact *= 1.2;
    }

    riskReduction = control.riskReduction;
    businessImpactChange = Object.values(control.businessImpact || {}).reduce((a, b) => a + b, 0) / 5;

    return {
      securityScore: Math.min(100, Math.max(0, gameState.securityScore + securityImpact)),
      complianceScore: Math.min(100, Math.max(0, gameState.complianceScore + complianceImpact)),
      riskLevel: Math.max(0, gameState.riskLevel - riskReduction * 0.5),
      businessImpact: Math.min(100, Math.max(0, gameState.businessImpact + businessImpactChange * 0.1))
    };
  };

  const completeScenario = useCallback(() => {
    const finalScore = calculateFinalScore();
    const results = {
      success: true,
      scenarioId: scenario.id,
      score: finalScore,
      objectives: gameState.objectives,
      securityComplianceBalance: (gameState.securityScore - gameState.complianceScore) / 100,
      learningOutcomes: generateLearningOutcomes(),
      recommendations: generateRecommendations(),
      timeUsed: scenario.timeLimit * 60 - gameState.timeRemaining,
      budgetUsed: scenario.costBudget - gameState.budgetRemaining,
      controlsImplemented: selectedControls
    };

    setShowResults(true);
    onComplete(results);
  }, [gameState, scenario, selectedControls]);

  const calculateFinalScore = (): number => {
    const objectiveScore = gameState.objectives.reduce((sum, obj) => {
      const completion = obj.currentValue / obj.targetValue;
      return sum + Math.min(1, completion) * 100;
    }, 0) / gameState.objectives.length;

    const balanceScore = 100 - Math.abs(gameState.securityScore - gameState.complianceScore);
    const resourceScore = (gameState.budgetRemaining / scenario.costBudget) * 50 +
                         (gameState.timeRemaining / (scenario.timeLimit * 60)) * 50;
    const riskScore = Math.max(0, 100 - gameState.riskLevel);
    const businessScore = gameState.businessImpact;

    return Math.round(
      objectiveScore * 0.3 +
      balanceScore * 0.2 +
      resourceScore * 0.15 +
      riskScore * 0.2 +
      businessScore * 0.15
    );
  };

  const generateLearningOutcomes = (): string[] => {
    const outcomes = [];

    if (Math.abs(gameState.securityScore - gameState.complianceScore) > 30) {
      outcomes.push('Need better balance between security and compliance investments');
    }

    if (gameState.budgetRemaining < scenario.costBudget * 0.2) {
      outcomes.push('Consider more cost-effective control implementation strategies');
    }

    if (gameState.riskLevel > 70) {
      outcomes.push('Risk management requires more attention');
    }

    outcomes.push(`Successfully implemented ${selectedControls.length} controls`);

    return outcomes;
  };

  const generateRecommendations = (): string[] => {
    const recommendations = [];

    if (gameState.securityScore > gameState.complianceScore + 20) {
      recommendations.push('Focus on compliance documentation and processes');
    }

    if (gameState.complianceScore > gameState.securityScore + 20) {
      recommendations.push('Invest more in security controls and monitoring');
    }

    if (gameState.businessImpact < 60) {
      recommendations.push('Prioritize controls that provide business value');
    }

    return recommendations;
  };

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const renderPlanningPhase = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Scenario Overview</h3>
        <p className="text-slate-300 mb-4">{scenario.description}</p>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div>
            <div className="text-sm text-slate-400 mb-1">Difficulty</div>
            <div className="text-lg font-bold capitalize text-yellow-400">{scenario.difficulty}</div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Time Limit</div>
            <div className="text-lg font-bold text-blue-400">{scenario.timeLimit} min</div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Budget</div>
            <div className="text-lg font-bold text-green-400">${(scenario.costBudget / 1000).toFixed(0)}k</div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Risk Tolerance</div>
            <div className="text-lg font-bold text-orange-400">{scenario.riskTolerance}%</div>
          </div>
        </div>

        <div>
          <h4 className="text-white font-medium mb-3">Objectives</h4>
          <div className="space-y-2">
            {scenario.objectives.map(obj => (
              <div key={obj.id} className="bg-slate-900/50 rounded-lg p-3">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-white">{obj.description}</span>
                  <span className="text-xs text-slate-400">{obj.currentValue}/{obj.targetValue}</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(obj.currentValue / obj.targetValue) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-white font-medium mb-3">Available Controls</h4>
          <div className="grid grid-cols-1 gap-3">
            {scenario.availableControls.map(controlId => {
              const control = soc2Manager.getControl(controlId);
              if (!control) return null;

              return (
                <div
                  key={controlId}
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedControls.includes(controlId)
                      ? 'border-blue-500 bg-blue-900/20'
                      : 'border-slate-600 bg-slate-800 hover:border-slate-500'
                  }`}
                  onClick={() => handleControlSelection(controlId)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h5 className="text-sm font-semibold text-white">
                        {control.number}: {control.title}
                      </h5>
                      <p className="text-xs text-slate-400 mt-1">{control.description}</p>
                    </div>
                    <div className={`px-2 py-1 text-xs rounded ${
                      control.category === 'security'
                        ? 'bg-red-900/50 text-red-400'
                        : 'bg-blue-900/50 text-blue-400'
                    }`}>
                      {control.category.replace('_', ' ')}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-slate-500">Cost:</span>
                      <span className="text-white ml-1">${(control.implementationCost / 1000).toFixed(0)}k</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Impact:</span>
                      <span className="text-green-400 ml-1">{control.riskReduction}%</span>
                    </div>
                    <div>
                      <span className="text-slate-500">Time:</span>
                      <span className="text-blue-400 ml-1">{control.implementation.timeline.estimatedDuration}d</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex justify-between items-center mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => setGameState(prev => ({ ...prev, currentPhase: 'execution' }))}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded transition-colors"
            disabled={selectedControls.length === 0}
          >
            Start Implementation ({selectedControls.length} controls selected)
          </button>
        </div>
      </div>
    </div>
  );

  const renderExecutionPhase = () => (
    <div className="space-y-6">
      {/* Status Bar */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <div className="grid grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-slate-400 mb-1">Time Remaining</div>
            <div className={`text-lg font-bold ${
              gameState.timeRemaining < 300 ? 'text-red-400' : 'text-green-400'
            }`}>
              {formatTime(gameState.timeRemaining)}
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Budget Remaining</div>
            <div className={`text-lg font-bold ${
              gameState.budgetRemaining < scenario.costBudget * 0.2 ? 'text-red-400' : 'text-green-400'
            }`}>
              ${(gameState.budgetRemaining / 1000).toFixed(0)}k
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Security Score</div>
            <div className="text-lg font-bold text-red-400">{gameState.securityScore}%</div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Compliance Score</div>
            <div className="text-lg font-bold text-blue-400">{gameState.complianceScore}%</div>
          </div>
        </div>
      </div>

      {/* Balance Indicator */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <h3 className="text-white font-medium mb-3">Security-Compliance Balance</h3>
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-red-400">Security</span>
              <span className="text-blue-400">Compliance</span>
            </div>
            <div className="relative h-6 bg-slate-700 rounded-full">
              <div
                className="absolute top-0 left-0 h-6 bg-gradient-to-r from-red-500 to-transparent rounded-full"
                style={{ width: `${gameState.securityScore}%` }}
              ></div>
              <div
                className="absolute top-0 right-0 h-6 bg-gradient-to-l from-blue-500 to-transparent rounded-full"
                style={{ width: `${gameState.complianceScore}%` }}
              ></div>
              <div
                className="absolute top-0 h-2 bg-white rounded-full"
                style={{
                  left: `${50 + (gameState.securityScore - gameState.complianceScore) / 2}%`,
                  transform: 'translateX(-50%)'
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Queue */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <h3 className="text-white font-medium mb-3">Implementation Queue</h3>
        <div className="space-y-2">
          {selectedControls.map(controlId => {
            const control = soc2Manager.getControl(controlId);
            const isImplemented = gameState.decisions.some(d => d.controlId === controlId);

            if (!control) return null;

            return (
              <div
                key={controlId}
                className={`bg-slate-900/50 rounded-lg p-3 border ${
                  isImplemented ? 'border-green-600' : 'border-slate-600'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h5 className="text-sm font-semibold text-white">
                      {control.number}: {control.title}
                    </h5>
                    <p className="text-xs text-slate-400">
                      Cost: ${(control.implementationCost / 1000).toFixed(0)}k |
                      Time: {control.implementation.timeline.estimatedDuration} days
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    {isImplemented ? (
                      <span className="text-xs px-2 py-1 bg-green-900/50 text-green-400 rounded">
                        Implemented
                      </span>
                    ) : (
                      <button
                        onClick={() => setActiveDecision({
                          controlId,
                          action: 'implement',
                          cost: control.implementationCost,
                          timeSpent: control.implementation.timeline.estimatedDuration * 8 * 60, // Convert to minutes
                          rationale: ''
                        })}
                        className="text-xs px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                        disabled={gameState.budgetRemaining < control.implementationCost || gameState.timeRemaining < 300}
                      >
                        Implement
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
        <h3 className="text-white font-medium mb-3">Current Risk Assessment</h3>
        <div className="space-y-2">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-slate-400">Overall Risk Level</span>
              <span className={`font-bold ${
                gameState.riskLevel > 70 ? 'text-red-400' :
                gameState.riskLevel > 40 ? 'text-yellow-400' : 'text-green-400'
              }`}>
                {gameState.riskLevel > 70 ? 'High' : gameState.riskLevel > 40 ? 'Medium' : 'Low'}
              </span>
            </div>
            <div className="w-full bg-slate-700 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  gameState.riskLevel > 70 ? 'bg-red-500' :
                  gameState.riskLevel > 40 ? 'bg-yellow-500' : 'bg-green-500'
                }`}
                style={{ width: `${gameState.riskLevel}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {activeDecision && (
        <div className="bg-slate-800 rounded-lg p-4 border border-blue-500">
          <h3 className="text-white font-medium mb-3">Confirm Decision</h3>
          <p className="text-slate-300 mb-4">
            Implement control: {soc2Manager.getControl(activeDecision.controlId)?.title}
          </p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <span className="text-slate-400">Cost: </span>
              <span className="text-white font-medium">${(activeDecision.cost / 1000).toFixed(0)}k</span>
            </div>
            <div>
              <span className="text-slate-400">Time: </span>
              <span className="text-white font-medium">{formatTime(activeDecision.timeSpent)}</span>
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => makeDecision(activeDecision)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded transition-colors"
            >
              Confirm
            </button>
            <button
              onClick={() => setActiveDecision(null)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderResults = () => (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 text-center">
        <h2 className="text-2xl font-bold text-white mb-2">Scenario Complete!</h2>
        <div className="text-4xl font-bold text-green-400 mb-4">{calculateFinalScore()} points</div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-sm text-slate-400 mb-1">Controls Implemented</div>
            <div className="text-lg font-bold text-blue-400">{selectedControls.length}</div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Time Used</div>
            <div className="text-lg font-bold text-white">
              {formatTime(scenario.timeLimit * 60 - gameState.timeRemaining)}
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Budget Used</div>
            <div className="text-lg font-bold text-green-400">
              ${((scenario.costBudget - gameState.budgetRemaining) / 1000).toFixed(0)}k
            </div>
          </div>
          <div>
            <div className="text-sm text-slate-400 mb-1">Balance Score</div>
            <div className="text-lg font-bold text-purple-400">
              {Math.abs(gameState.securityScore - gameState.complianceScore)}%
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Learning Outcomes</h3>
          <div className="space-y-2">
            {generateLearningOutcomes().map((outcome, index) => (
              <div key={index} className="flex items-start space-x-2">
                <span className="text-green-400 mt-1">✓</span>
                <span className="text-sm text-slate-300">{outcome}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4">Recommendations</h3>
          <div className="space-y-2">
            {generateRecommendations().map((rec, index) => (
              <div key={index} className="flex items-start space-x-2">
                <span className="text-blue-400 mt-1">→</span>
                <span className="text-sm text-slate-300">{rec}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex justify-center space-x-4">
        <button
          onClick={() => setShowResults(false)}
          className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded transition-colors"
        >
          Review Details
        </button>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded transition-colors"
        >
          Complete
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="border-b border-slate-700 p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white">{scenario.title}</h2>
              <p className="text-slate-400 mt-1">{scenario.category.replace('_', ' ').toUpperCase()}</p>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {showResults ? renderResults() :
           gameState.currentPhase === 'planning' ? renderPlanningPhase() :
           renderExecutionPhase()}
        </div>
      </div>
    </div>
  );
};

// Supporting interfaces
interface GameState {
  timeRemaining: number;
  budgetRemaining: number;
  decisions: PlayerDecision[];
  objectives: (ScenarioObjective & { currentValue: number })[];
  currentPhase: 'planning' | 'execution' | 'completed';
  score: number;
  securityScore: number;
  complianceScore: number;
  riskLevel: number;
  businessImpact: number;
}

interface DecisionState {
  controlId: string;
  action: string;
  cost: number;
  timeSpent: number;
  rationale: string;
}

export default SOC2ScenarioPlayer;